import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
    FiTarget,
    FiEye,
    FiCheckCircle,
    FiShield,
    FiUsers,
    FiAward
} from 'react-icons/fi';
import {
    HiOutlineLightBulb,
    HiOutlineHeart,
    HiOutlineShieldCheck,
    HiOutlineUserGroup,
    HiOutlineScale,
    HiOutlineStar,
    HiOutlineClock,
    HiOutlineChat
} from 'react-icons/hi';
import SectionHeading from '../components/SectionHeading';
import SEO from '../components/SEO';
import IsoBadge from '../components/IsoBadge';

const About = () => {
    const { t } = useTranslation();

    const coreValues = [
        { icon: HiOutlineShieldCheck, title: t('about.values.items.integrity.title'), description: t('about.values.items.integrity.desc') },
        { icon: HiOutlineStar, title: t('about.values.items.excellence.title'), description: t('about.values.items.excellence.desc') },
        { icon: HiOutlineScale, title: t('about.values.items.responsibility.title'), description: t('about.values.items.responsibility.desc') },
        { icon: HiOutlineHeart, title: t('about.values.items.accountability.title'), description: t('about.values.items.accountability.desc') },
        { icon: HiOutlineUserGroup, title: t('about.values.items.respect.title'), description: t('about.values.items.respect.desc') },
        { icon: HiOutlineLightBulb, title: t('about.values.items.innovation.title'), description: t('about.values.items.innovation.desc') },
        { icon: HiOutlineClock, title: t('about.values.items.discipline.title'), description: t('about.values.items.discipline.desc') },
        { icon: HiOutlineChat, title: t('about.values.items.teamwork.title'), description: t('about.values.items.teamwork.desc') },
    ];


    const qualityPillars = [
        { icon: FiUsers, title: t('about.cert.items.training') },
        { icon: FiAward, title: t('about.cert.items.irata') },
    ];
    

    return (
        <div className="w-full">
            <SEO
                title={t('seo.about.title')}
                description={t('seo.about.description')}
                keywords="about primeforce, cleaning company bahrain, facility management bahrain, rope access technicians"
            />

            {/* Hero Banner */}
            <section className="relative pt-28 pb-12 md:pt-36 md:pb-16 overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="/images/site/hero-facility-team.png"
                        alt="PrimeForce Services Team"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/75 to-black/85"></div>
                </div>

                <div className="container-custom relative z-10 text-center">
                    <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="inline-block px-4 py-1.5 bg-accent-500/20 text-accent-300 rounded-full text-xs sm:text-sm font-semibold mb-4 border border-accent-500/30 backdrop-blur-sm"
                    >
                        {t('about.hero.badge')}
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight"
                    >
                        {t('about.hero.titleStart')} <span className="text-accent-400">{t('about.hero.titleAccent')}</span> {t('about.hero.titleEnd')}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="text-sm sm:text-base md:text-lg text-gray-200 max-w-3xl mx-auto leading-relaxed"
                    >
                        {t('about.hero.subtext')}
                    </motion.p>
                </div>
            </section>

            {/* Company Story */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-600 rounded-full text-sm font-semibold mb-4">
                                {t('about.story.badge')}
                            </span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                                {t('about.story.titleStart')} <span className="text-primary-600">{t('about.story.titleAccent')}</span>
                            </h2>
                            <div className="space-y-4 text-gray-600 leading-relaxed text-base md:text-lg">
                                <p>{t('about.story.p1')}</p>
                                <p>{t('about.story.p2')}</p>
                                <p>{t('about.story.p3')}</p>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="relative"
                        >
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                                <img
                                    src="/images/site/facility-support.jpg"
                                    alt="PrimeForce Team"
                                    loading="lazy"
                                    className="w-full h-[480px] object-cover"
                                />
                            </div>
                            {/* Floating Card */}
                            <div className="absolute -bottom-6 left-0 md:-bottom-6 md:-left-6 bg-accent-500 text-white p-6 rounded-2xl shadow-xl rtl:right-0 rtl:left-auto md:rtl:-right-6 md:rtl:-left-auto">
                                <div className="text-3xl md:text-4xl font-bold">{t('about.story.cardTitle')}</div>
                                <div className="text-sm md:text-base text-accent-100 font-medium">{t('about.story.cardSubtitle')}</div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Vision & Mission */}
            <section className="section-padding bg-gray-50">
                <div className="container-custom">
                    <SectionHeading
                        subtitle={t('about.mission.subtitle')}
                        title={t('about.mission.title')}
                        description={t('about.mission.description')}
                    />

                    <div className="grid md:grid-cols-2 gap-8">
                        {/* Vision Card */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-shadow border border-gray-100"
                        >
                            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                                <FiEye className="text-white" size={28} />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">{t('about.mission.visionTitle')}</h3>
                            <p className="text-gray-600 leading-relaxed text-lg">
                                {t('about.mission.visionDesc')}
                            </p>
                        </motion.div>

                        {/* Mission Card */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-shadow border border-gray-100"
                        >
                            <div className="w-16 h-16 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                                <FiTarget className="text-white" size={28} />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">{t('about.mission.missionTitle')}</h3>
                            <p className="text-gray-600 leading-relaxed text-lg">
                                {t('about.mission.missionDesc')}
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Core Values */}
            <section className="section-padding bg-primary-600 relative overflow-hidden">
                <div className="container-custom relative z-10">
                    <SectionHeading
                        subtitle={t('about.values.subtitle')}
                        title={t('about.values.title')}
                        description={t('about.values.description')}
                        light={true}
                    />

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {coreValues.map((value, index) => (
                            <motion.div
                                key={value.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.05 }}
                                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-all duration-300 group border border-white/10"
                            >
                                <div className="w-12 h-12 mx-auto mb-4 bg-accent-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <value.icon className="text-white" size={24} />
                                </div>
                                <h4 className="text-lg font-semibold text-white mb-2">{value.title}</h4>
                                <p className="text-sm text-white/80">{value.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Quality & Standards Section */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <SectionHeading
                        subtitle={t('about.cert.subtitle')}
                        title={t('about.cert.title')}
                        description={t('about.cert.description')}
                    />

                    {/* ISO Certified Badges */}
                    <div className="grid md:grid-cols-3 gap-8 mb-12">
                        {[
                            { number: "9001", year: "2015", title: t('about.cert.items.iso9001') },
                            { number: "14001", year: "2015", title: t('about.cert.items.iso14001') },
                            { number: "45001", year: "2018", title: t('about.cert.items.iso45001') },
                        ].map((item, index) => (
                            <motion.div
                                key={item.number}
                                initial={{ opacity: 0, y: 25 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                                className="bg-gradient-to-b from-gray-50 via-white to-gray-50/50 border border-gray-200 rounded-3xl p-6 shadow-sm hover:shadow-xl hover:border-primary-300 transition-all text-center flex flex-col items-center justify-between group"
                            >
                                <div className="py-2">
                                    <IsoBadge number={item.number} year={item.year} className="w-36 h-36 mx-auto" />
                                </div>
                                <div className="mt-4">
                                    <span className="inline-block px-3 py-1 bg-primary-50 text-primary-700 font-bold text-xs rounded-full mb-2 border border-primary-100">
                                        ISO {item.number}:{item.year}
                                    </span>
                                    <h4 className="text-base font-bold text-gray-800 leading-snug">{item.title}</h4>
                                </div>
                                <div className="mt-4 pt-3 border-t border-gray-100 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-emerald-600">
                                    <FiCheckCircle size={15} /> Certified Standard
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <div className="flex flex-wrap justify-center gap-6">
                        {qualityPillars.map((item, index) => (
                            <motion.div
                                key={item.title}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                                className="flex items-center gap-3 bg-gray-50 border border-gray-200 px-6 py-4 rounded-2xl hover:bg-primary-50 hover:border-primary-300 transition-all shadow-sm"
                            >
                                <item.icon className="text-primary-600 flex-shrink-0" size={24} />
                                <span className="font-semibold text-gray-800">{item.title}</span>
                            </motion.div>
                        ))}
                    </div>

                    {/* Quality Commitment Footer Note */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="mt-12 bg-primary-50 border border-primary-100 rounded-2xl p-8 text-center max-w-4xl mx-auto"
                    >
                        <p className="text-gray-700 leading-relaxed text-base md:text-lg">
                            {t('about.cert.footer')}
                        </p>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default About;
